import { Button } from '@/components/ui/Button';
import { Department, Team, SurveyRespondent } from '@/types/database';
import { Pencil, Trash2, ExternalLink } from 'lucide-react';

interface RespondentTableProps {
  respondents: SurveyRespondent[];
  departments: Department[];
  teams: Team[];
  surveyId: string;
  onEdit: (respondent: SurveyRespondent) => void;
  onDelete: (id: string) => void;
}

export function RespondentTable({
  respondents,
  departments,
  teams,
  surveyId,
  onEdit,
  onDelete
}: RespondentTableProps) {
  const getSurveyUrl = (respondentId: string) =>
    `${window.location.origin}/surveys/${surveyId}/respond/${respondentId}`;

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      <div className="min-w-full overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                Name
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                Email
              </th>
              <th scope="col" className="hidden sm:table-cell px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                Department
              </th>
              <th scope="col" className="hidden sm:table-cell px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                Team
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                Survey Link
              </th>
              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {respondents.map((respondent) => (
              <tr key={respondent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {respondent.first_name} {respondent.last_name}
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
                    {respondent.email}
                  </div>
                </td>
                <td className="hidden sm:table-cell px-3 py-2 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {departments.find(d => d.id === respondent.department_id)?.name || '-'}
                  </div>
                </td>
                <td className="hidden sm:table-cell px-3 py-2 whitespace-nowrap">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {teams.find(t => t.id === respondent.team_id)?.name || '-'}
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <a
                      href={getSurveyUrl(respondent.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open survey"
                    >
                      <Button variant="outline" size="sm" type="button">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(respondent)}
                      title="Edit respondent"
                      className="hidden sm:inline-flex"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(respondent.id)}
                      className="hidden sm:inline-flex text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      title="Delete respondent"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {/* Mobile actions */}
                    <div className="sm:hidden flex items-center space-x-1">
                      <button
                        onClick={() => onEdit(respondent)}
                        className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(respondent.id)}
                        className="p-1 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}